// Cross-device sync for the wardrobe + Bones wallet (Supabase table
// `user_wardrobe`, owner-only RLS). localStorage stays the instant source of
// truth; this module mirrors it to Supabase and reconciles on login.
//
// Reconciliation rules (last-write-wins by `updated_at`, but loss-averse):
//   - owned   : UNION(local, remote)  — never lose an outfit you bought
//   - ledger  : UNION(local, remote)  — more awarded keys = stricter anti-farm
//   - lifetime: MAX(local, remote)    — lifetime Bones is monotonic
//   - balance : the side updated more recently wins
//   - equipped: the side updated more recently wins
// Then the reconciled state is pushed back so both devices converge.

import { supabase } from "@/integrations/supabase/client"
import {
  getOwned,
  getEquipped,
  applyRemoteWardrobe,
  WARDROBE_EVENT,
} from "./wardrobe-store"
import {
  getBonesWallet,
  getBonesLedger,
  applyRemoteBones,
  BONES_EVENT,
} from "./bones-store"
import { mergeWardrobe, type WardrobeState, type RemoteRow } from "./wardrobe-merge"

const SYNCED_AT_KEY = "pawket.wardrobe.syncedAt"
const PUSH_DEBOUNCE_MS = 900

function snapshot(): WardrobeState {
  const w = getBonesWallet()
  return {
    owned: getOwned(),
    equipped: getEquipped(),
    balance: w.balance,
    lifetime: w.lifetime,
    ledger: getBonesLedger(),
  }
}

function getSyncedAt(): string | null {
  try { return localStorage.getItem(SYNCED_AT_KEY) } catch { return null }
}
function setSyncedAt(ts: string | null): void {
  try { if (ts) localStorage.setItem(SYNCED_AT_KEY, ts) } catch { /* no-op */ }
}

async function getUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getUser()
    return data?.user?.id ?? null
  } catch {
    return null
  }
}

// Suppress the write-through while we're applying a remote snapshot locally, so
// the events that applyRemote* emit don't bounce straight back to the server.
let suspendSync = false

async function pushRemote(userId: string, state: WardrobeState): Promise<void> {
  const { data, error } = await supabase
    .from("user_wardrobe")
    .upsert(
      {
        user_id: userId,
        owned: state.owned,
        equipped: state.equipped as unknown as Record<string, unknown>,
        bones_balance: Math.max(0, Math.round(state.balance)),
        bones_lifetime: Math.max(0, Math.round(state.lifetime)),
        bones_ledger: state.ledger as unknown as Record<string, unknown>,
      } as never,
      { onConflict: "user_id" },
    )
    .select("updated_at")
    .single()
  if (!error && data && (data as { updated_at?: string }).updated_at) {
    setSyncedAt((data as { updated_at: string }).updated_at)
  }
}

let hydrated = false
let hydratePromise: Promise<void> | null = null

async function doHydrate(): Promise<void> {
  const userId = await getUserId()
  if (!userId) return
  const local = snapshot()
  try {
    const { data, error } = await supabase
      .from("user_wardrobe")
      .select("owned, equipped, bones_balance, bones_lifetime, bones_ledger, updated_at")
      .eq("user_id", userId)
      .maybeSingle()
    if (error) throw error

    if (!data) {
      // No remote row yet — seed it from this device's local state.
      await pushRemote(userId, local)
      return
    }

    const { merged, changedLocal } = mergeWardrobe(local, data as RemoteRow, getSyncedAt())
    if (changedLocal) {
      suspendSync = true
      try {
        applyRemoteWardrobe({ owned: merged.owned, equipped: merged.equipped })
        applyRemoteBones({ balance: merged.balance, lifetime: merged.lifetime, ledger: merged.ledger })
      } finally {
        suspendSync = false
      }
    }
    // Push the reconciled state up so the server converges (union owned/ledger,
    // max lifetime), and record the fresh updated_at as our sync watermark.
    await pushRemote(userId, merged)
  } catch {
    // Network/permission failure — keep local state; we'll retry on next change.
  }
}

/** Hydrate once per session (dedupes in-flight calls). */
export function ensureWardrobeSynced(): Promise<void> {
  if (hydrated) return Promise.resolve()
  if (!hydratePromise) {
    hydratePromise = doHydrate()
      .then(() => { hydrated = true })
      .finally(() => { hydratePromise = null })
  }
  return hydratePromise
}

/** Start hydrate + write-through. Returns a teardown for the listeners. */
export function initWardrobeSync(): () => void {
  void ensureWardrobeSynced()

  let timer: ReturnType<typeof setTimeout> | null = null
  const onChange = () => {
    if (suspendSync || !hydrated) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(async () => {
      timer = null
      const userId = await getUserId()
      if (userId) await pushRemote(userId, snapshot()).catch(() => {})
    }, PUSH_DEBOUNCE_MS)
  }

  window.addEventListener(WARDROBE_EVENT, onChange)
  window.addEventListener(BONES_EVENT, onChange)
  return () => {
    if (timer) clearTimeout(timer)
    window.removeEventListener(WARDROBE_EVENT, onChange)
    window.removeEventListener(BONES_EVENT, onChange)
  }
}

/** Test-only reset of module state. */
export function __resetWardrobeSyncForTests(): void {
  hydrated = false
  hydratePromise = null
  suspendSync = false
}
